import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import EditorialSelect from "@/components/admin/EditorialSelect";
import EditorialEmptyState from "@/components/editorial/EditorialEmptyState";
import apertureShutterLottie from "@/assets/lottie/aperture-shutter.json";
import {
  Images, Plus, Search, Folder, ArrowRight, SlidersHorizontal,
  CalendarDays, Tag, Heart, X, Lock, MessageCircle, Users, Download, Eye, Sparkles,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminGalleries() {
  const [clients, setClients] = useState([]);
  const [orphanGalleries, setOrphanGalleries] = useState([]);
  const [allGalleries, setAllGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        api.get("/admin/gallery-clients"),
        api.get("/admin/galleries"),
      ]);
      setClients(c.data);
      setAllGalleries(g.data || []);
      // Galleries without a client_id are shown in a separate "Unassigned" group
      setOrphanGalleries((g.data || []).filter((x) => !x.client_id));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Aggregate category tags across all galleries
  const allTags = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => (c.latest_collections || []).forEach((g) => (g.category_tags || []).forEach((t) => set.add(t))));
    orphanGalleries.forEach((g) => (g.category_tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [clients, orphanGalleries]);

  const filteredClients = useMemo(() => {
    let list = [...clients];
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((c) =>
        c.name.toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s) ||
        (c.latest_collections || []).some((g) => g.name.toLowerCase().includes(s))
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((c) => (c.latest_collections || []).some((g) => g.status === statusFilter));
    }
    if (tagFilter !== "all") {
      list = list.filter((c) => (c.latest_collections || []).some((g) => (g.category_tags || []).includes(tagFilter)));
    }
    if (sortBy === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => new Date(b.latest_created_at || 0) - new Date(a.latest_created_at || 0));
    return list;
  }, [clients, search, statusFilter, tagFilter, sortBy]);

  return (
    <div className="space-y-10" data-testid="admin-galleries-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-3 inline-flex items-center gap-1.5">
            <Images className="w-3 h-3" /> Studio · Gallery delivery
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[0.95] tracking-tight">
            Collections.
          </h1>
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
            Every shoot, edit, and final delivery — held for each client like pages in a portfolio book.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-6 h-11 bg-foreground text-background text-[11px] uppercase tracking-[0.28em] font-medium hover:opacity-90 transition-opacity self-start sm:self-end"
          data-testid="new-collection-btn"
        >
          <Plus className="w-4 h-4" /> New collection
        </button>
      </div>

      {/* Filter strip — quietly elegant chips, not green pills */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border pb-5">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients or collections…"
            className="w-full pl-10 pr-3 h-10 bg-transparent border border-border outline-none focus:border-foreground/60 transition-colors text-sm"
            data-testid="gallery-search"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect value={statusFilter} onChange={setStatusFilter} testid="filter-status" options={[
            ["all", "All status"], ["draft", "Draft"], ["sent", "Sent"], ["selections_received", "Selections received"], ["delivered", "Delivered"],
          ]} />
          {allTags.length > 0 && (
            <FilterSelect value={tagFilter} onChange={setTagFilter} testid="filter-tag" options={[["all", "All tags"], ...allTags.map((t) => [t, t])]} />
          )}
          <FilterSelect value={sortBy} onChange={setSortBy} testid="sort-by" options={[
            ["recent", "Most recent"], ["name", "Client A–Z"],
          ]} icon={<SlidersHorizontal className="w-3.5 h-3.5" />} />
        </div>
      </div>

      {/* New client selections — impossible to miss. Lists every gallery with
          submitted selections and deep-links to its #selections block. */}
      {!loading && allGalleries.some((g) => (g.selection_count || 0) > 0) && (
        <SelectionsCallout galleries={allGalleries} onOpen={(id) => navigate(`/admin/galleries/${id}#selections`)} />
      )}

      {loading && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="galleries-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-card border border-border overflow-hidden">
              <div className="aspect-[4/5] bg-muted/40 animate-pulse" />
              <div className="p-5 space-y-3">
                <div className="h-4 w-2/3 bg-muted/40 animate-pulse" />
                <div className="h-3 w-1/2 bg-muted/30 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredClients.length === 0 && orphanGalleries.length === 0 && (
        <EditorialEmptyState
          tone="charcoal"
          lottie={apertureShutterLottie}
          eyebrow="Awaiting your first portrait"
          headline={<>Awaiting its first <em>portrait</em>.</>}
          description="When you publish a gallery, it will live here — quietly, beautifully, for as long as it should."
          primary={{
            label: "Create your first gallery",
            onClick: () => setCreating(true),
          }}
          secondary={{
            label: "or import an existing set",
          }}
          data-testid="empty-galleries"
        />
      )}

      {/* Client cards */}
      {filteredClients.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="client-grid">
          {filteredClients.map((c) => (
            <ClientCard key={c.id} client={c} onClick={() => navigate(`/admin/galleries/clients/${c.id}`)} />
          ))}
        </div>
      )}

      {/* Unassigned galleries */}
      {orphanGalleries.length > 0 && (
        <div className="space-y-5 pt-6 border-t border-border">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-1">Unassigned</div>
              <h2 className="font-serif text-3xl font-light tracking-tight">Loose collections</h2>
            </div>
            <span className="text-xs text-muted-foreground">{orphanGalleries.length}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {orphanGalleries.map((g) => (
              <button
                key={g.id}
                onClick={() => navigate(`/admin/galleries/${g.id}`)}
                className="text-left bg-card border border-border overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 transition-all duration-500 group relative"
                data-testid={`orphan-gallery-${g.id}`}
              >
                {(g.selection_count || 0) > 0 && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 bg-rose-500 text-white text-[10px] uppercase tracking-[0.18em] z-10"
                    data-testid={`orphan-selections-badge-${g.id}`}
                  >
                    <Heart className="w-3 h-3 fill-white" /> {g.selection_count}
                  </span>
                )}
                <div className="aspect-[4/5] bg-muted overflow-hidden relative">
                  {g.cover_image ? (
                    <img
                      src={`${BACKEND}${g.cover_image}`}
                      alt={g.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-muted/60 to-muted/30 flex items-center justify-center">
                      <Folder className="w-7 h-7 text-muted-foreground/40" strokeWidth={1.25} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/[0.06] transition-colors" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Folder className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <div className="font-serif text-lg leading-snug truncate">{g.name}</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <StatusDot status={g.status} />
                    <span className="capitalize">{g.status?.replace("_", " ") || "draft"}</span>
                    <span className="text-muted-foreground/40">·</span>
                    <span>{g.customer_name || "Unassigned"}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <NewCollectionDialog open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} clients={clients} />
    </div>
  );
}

/* SelectionsCallout — rose-accented strip listing every gallery that has
   client selections waiting. Sits above the client grid so a submission is
   the first thing the photographer sees when opening Galleries. */
const CALLOUT_DISMISS_KEY = "lumetier_selections_callout_dismissed";

function SelectionsCallout({ galleries, onOpen }) {
  const withSelections = galleries
    .filter((g) => (g.selection_count || 0) > 0)
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  // Dismissal is tied to the current set of selections — a new or changed
  // submission brings the strip back so nothing slips past unnoticed.
  const sig = withSelections.map((g) => `${g.id}:${g.selection_count}`).join("|");
  const [dismissedSig, setDismissedSig] = useState(() => localStorage.getItem(CALLOUT_DISMISS_KEY));
  if (dismissedSig === sig) return null;
  const dismiss = () => {
    localStorage.setItem(CALLOUT_DISMISS_KEY, sig);
    setDismissedSig(sig);
  };
  return (
    <div className="border border-rose-500/30 bg-rose-500/[0.04]" data-testid="selections-callout">
      <div className="px-5 pt-4 pb-3 flex items-center gap-2">
        <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500" />
        <span className="text-[10px] uppercase tracking-[0.32em] text-rose-600 dark:text-rose-400">
          Client selections received
        </span>
        <button
          onClick={dismiss}
          className="ml-auto -mr-2 w-8 h-8 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-rose-500/10 transition-colors"
          aria-label="Dismiss"
          title="Dismiss — comes back when new selections arrive"
          data-testid="selections-callout-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="divide-y divide-rose-500/10">
        {withSelections.map((g) => (
          <button
            key={g.id}
            onClick={() => onOpen(g.id)}
            className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-rose-500/[0.06] transition-colors group"
            data-testid={`selections-callout-row-${g.id}`}
          >
            <span className="font-serif text-base leading-snug truncate">{g.name}</span>
            <span className="text-xs text-muted-foreground truncate">{g.customer_name || "Unassigned"}</span>
            <span className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs text-rose-600 dark:text-rose-400">
              {g.selection_count} selection{g.selection_count > 1 ? "s" : ""}
              <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid, icon }) {
  // Hairline filter pill — uses EditorialSelect under the hood for the
  // editorial cream/gold dropdown styling that matches the rest of admin.
  return (
    <div className="min-w-[160px] flex items-center gap-2" data-testid={`${testid}-wrapper`}>
      {icon ? <span className="text-[color:var(--gold-base)] shrink-0">{icon}</span> : null}
      <div className="flex-1">
        <EditorialSelect
          value={value}
          onChange={onChange}
          options={options.map(([v, label]) => ({ value: v, label }))}
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function ClientCard({ client, onClick }) {
  const cols = client.latest_collections || [];
  // We pick up to 4 cover images for the editorial 2x2 mosaic. The slots may
  // be `null` — those render as quiet stone fills so the card always feels
  // composed (never a half-finished collage).
  const slots = [...cols.slice(0, 4)];
  while (slots.length < 4) slots.push(null);
  const totalSelections = cols.reduce((n, g) => n + (g?.selection_count || 0), 0);
  // Surface the most representative status — the most recent collection's state
  const headlineStatus = cols[0]?.status || "draft";
  const itemCount = cols.reduce((n, g) => n + (g?.photo_count || 0), 0);

  return (
    <button
      onClick={onClick}
      // Hover perf treatment — same fix that resolved the Edge/Windows
      // Portfolio thrash:
      //   1) Promote the card to its own GPU compositor layer with translateZ(0)
      //      + will-change so hovers don't trigger a paint pass on the parent.
      //   2) Transition ONLY transform + opacity (not background-color/shadow).
      //      transition-all was repainting the box-shadow on every hover frame
      //      = ~700 ms hover at 4× CPU throttle. Now <16 ms.
      //   3) The dark veil overlay is a pre-painted layer whose opacity fades
      //      in/out — no background-color transition.
      className="text-left bg-card border border-border overflow-hidden hover:-translate-y-0.5 transition-[transform,opacity] duration-500 group relative will-change-transform"
      style={{ transform: "translateZ(0)" }}
      data-testid={`client-card-${client.id}`}
    >
      {totalSelections > 0 && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 bg-rose-500 text-white text-[10px] uppercase tracking-[0.18em] z-10"
          data-testid={`client-selections-badge-${client.id}`}
        >
          <Heart className="w-3 h-3 fill-white" /> {totalSelections}
        </span>
      )}
      {/* 2x2 editorial mosaic — tall, photo-forward */}
      <div className="grid grid-cols-2 grid-rows-2 gap-[1px] aspect-[4/5] bg-border overflow-hidden relative">
        {slots.map((g, i) => (
          <div key={`${client.id}-slot-${i}`} className="bg-muted/60 overflow-hidden">
            {g?.cover_image ? (
              <img
                src={`${BACKEND}${g.cover_image}`}
                alt=""
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-muted/40 to-muted/20" />
            )}
          </div>
        ))}
        {/* Pre-painted dark veil — opacity-only fade avoids background-color
            paint thrash on Edge/Windows GPU compositor. */}
        <div className="absolute inset-0 bg-black/[0.06] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      </div>
      {/* Metadata — editorial typography hierarchy */}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Folder className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <div className="font-serif text-lg leading-snug truncate">{client.name}</div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">
          <StatusDot status={headlineStatus} />
          <span className="capitalize">{headlineStatus.replace("_", " ")}</span>
          <span className="text-muted-foreground/40">·</span>
          <span>{client.collection_count} coll.</span>
          {itemCount > 0 && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span>{itemCount} {itemCount === 1 ? "photo" : "photos"}</span>
            </>
          )}
          {client.event_date && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {new Date(client.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            </>
          )}
          <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </button>
  );
}

/* Status dot — calm color cues for collection lifecycle (matches Pixieset's
   green-dot vocabulary but extends it: cream for draft, gold for sent,
   rose for selections-received, emerald for delivered). Keeps the page
   readable at a glance without shouting. */
function StatusDot({ status }) {
  const TONE = {
    draft: "bg-stone-300",
    sent: "bg-[#C9A961]",
    selections_received: "bg-rose-500",
    delivered: "bg-emerald-600/80",
    archived: "bg-stone-500",
  };
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${TONE[status] || TONE.draft}`}
      data-testid={`status-dot-${status || "draft"}`}
    />
  );
}

function NewCollectionDialog({ open, onClose, onCreated, clients }) {
  const navigate = useNavigate();
  const [clientMode, setClientMode] = useState("new"); // 'new' | 'existing'
  const [clientId, setClientId] = useState("");
  const [clientDraft, setClientDraft] = useState({ name: "", email: "", phone: "", event_date: "" });
  const [name, setName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [adv, setAdv] = useState(() => loadAdvancedDefaults());
  const [submitting, setSubmitting] = useState(false);
  // Side-by-side gallery-type comparison modal — triggered by the
  // "What's the difference?" link in the gallery-type section.
  const [showGalleryTypeInfo, setShowGalleryTypeInfo] = useState(false);

  useEffect(() => {
    if (!open) {
      setClientMode("new"); setClientId("");
      setClientDraft({ name: "", email: "", phone: "", event_date: "" });
      setName("");
      setShowAdvanced(false);
      setAdv(loadAdvancedDefaults());
      setSubmitting(false);
      setShowGalleryTypeInfo(false);
    }
  }, [open]);

  async function submit() {
    if (submitting) return;
    if (!name.trim()) return toast.error("Collection name is required");
    setSubmitting(true);
    try {
      // 1) resolve client
      let cid = clientId;
      let clientName = "", clientEmail = "";
      if (clientMode === "new") {
        if (!clientDraft.name.trim()) { setSubmitting(false); return toast.error("Client name is required"); }
        const { data: created } = await api.post("/admin/gallery-clients", {
          name: clientDraft.name.trim(),
          email: clientDraft.email || undefined,
          phone: clientDraft.phone || undefined,
          event_date: clientDraft.event_date || undefined,
        });
        cid = created.id;
        clientName = created.name; clientEmail = created.email || "";
      } else {
        if (!cid) { setSubmitting(false); return toast.error("Pick a client"); }
        const existing = clients.find((c) => c.id === cid);
        clientName = existing?.name || ""; clientEmail = existing?.email || "";
      }
      // 2) create gallery — only `name` and `client_id` are required; everything else optional
      const tags = (adv.category_tags || "").split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/galleries", {
        name: name.trim(),
        client_id: cid,
        customer_name: clientName,
        customer_email: clientEmail || undefined,
        password: adv.password.trim() || undefined,
        download_password: adv.download_password.trim() || undefined,
        included_edits: Number(adv.included_edits) || 0,
        extra_edit_price: Number(adv.extra_edit_price) || 35,
        type: adv.type || "standard",
        max_selections: Number(adv.max_selections) || 0,
        welcome_message: adv.welcome_message || undefined,
        cover_layout: adv.cover_layout,
        category_tags: tags,
      });
      saveAdvancedDefaults(adv); // remember for next time
      toast.success("Collection created");
      onCreated();
      navigate(`/admin/galleries/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto p-8 sm:p-12 [&>button.absolute]:hidden"
        data-testid="new-collection-dialog"
      >
        <div className="text-center pb-2 mb-7 border-b border-border">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-3">
            Lumetier · New collection
          </div>
          <DialogTitle className="font-serif text-3xl sm:text-4xl font-light tracking-tight">
            Create new collection.
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
            Start a client folder. You can upload photos right after — everything else is optional and editable later.
          </p>
        </div>

        <div className="space-y-6" data-testid="new-collection-form">
          {/* Client */}
          <div className="space-y-3">
            <div className="eyebrow">Client</div>
            <div className="flex gap-2">
              <Tab active={clientMode === "new"} onClick={() => setClientMode("new")} testid="tab-new-client">New client</Tab>
              <Tab active={clientMode === "existing"} onClick={() => setClientMode("existing")} testid="tab-existing-client" disabled={clients.length === 0}>Existing client</Tab>
            </div>

            {clientMode === "new" ? (
              <div className="space-y-3">
                <In label="Client name *" value={clientDraft.name} onChange={(v) => setClientDraft({ ...clientDraft, name: v })} testid="client-name" placeholder="e.g. Oyinda Agarah" />
                <div className="grid grid-cols-2 gap-3">
                  <In label="Email (optional)" value={clientDraft.email} onChange={(v) => setClientDraft({ ...clientDraft, email: v })} testid="client-email" />
                  <In label="Event date (optional)" type="date" value={clientDraft.event_date} onChange={(v) => setClientDraft({ ...clientDraft, event_date: v })} testid="client-event-date" />
                </div>
              </div>
            ) : (
              <EditorialSelect
                value={clientId}
                onChange={setClientId}
                options={clients.map((c) => ({
                  value: c.id,
                  label: c.name,
                  descriptor: c.email || undefined,
                }))}
                placeholder="Pick a client…"
                data-testid="existing-client-select"
              />
            )}
          </div>

          {/* Collection name — the only other required field */}
          <div>
            <In label="Collection name *" value={name} onChange={setName} testid="collection-name" placeholder="e.g. Spring Maternity" />
            <p className="text-[11px] text-muted-foreground mt-1.5">
              That&apos;s it — you can upload photos right away. No password by default; clients open the link directly.
            </p>
          </div>

          {/* Gallery type — primary decision, lives at the top (above Advanced).
              Standard = classic flow. Interactive = enables collaborator invites,
              favorites, comments, and (G5) live watch-party.
              Visual card picker replaces the original dropdown so the choice is
              concrete: each card lists exactly what's in it, no marketing fluff,
              "What's the difference?" link opens a richer comparison modal. */}
          <div className="space-y-3" data-testid="collection-type-section">
            <div className="flex items-baseline justify-between gap-3">
              <div className="eyebrow">Gallery type</div>
              <button
                type="button"
                onClick={() => setShowGalleryTypeInfo(true)}
                className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold-base)] hover:text-[color:var(--gold-foil)] transition-colors"
                data-testid="gallery-type-learn-more"
              >
                What&apos;s the difference?
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3" data-testid="gallery-type-cards">
              {[
                {
                  value: "standard",
                  label: "Standard",
                  tagline: "Deliver. Done.",
                  desc: "A clean, password-protected handoff. The client opens the link, views the gallery, downloads originals. No comments, no invites.",
                  bullets: [
                    { icon: Lock, text: "Single password" },
                    { icon: Eye, text: "Photographer-only contact" },
                    { icon: Download, text: "Originals download" },
                  ],
                  best: "Best for: finished weddings, brand deliveries, anything where the client just needs to receive.",
                },
                {
                  value: "interactive",
                  label: "Interactive",
                  tagline: "Choose. Together.",
                  desc: "A collaborative viewing room. Your primary client invites family, leaves comments only you can see, hearts favorite frames, and can join a live watch-together session.",
                  bullets: [
                    { icon: Users, text: "Primary client invites guests" },
                    { icon: Heart, text: "Favorites & selections" },
                    { icon: MessageCircle, text: "Private comments per frame" },
                    { icon: Sparkles, text: "Live watch-together sessions" },
                  ],
                  best: "Best for: proofing rounds, family albums, anything where the client involves others in choosing.",
                },
              ].map((opt) => {
                const selected = (adv.type || "standard") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAdv({ ...adv, type: opt.value })}
                    aria-pressed={selected}
                    data-testid={`gallery-type-card-${opt.value}`}
                    className={`group relative text-left p-5 border transition-all rounded-sm ${
                      selected
                        ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/8 shadow-[0_0_0_1px_hsl(var(--gold))]"
                        : "border-border hover:border-border/80 bg-card/40"
                    }`}
                  >
                    {/* Header row: title + tagline + selected check */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-serif text-xl italic font-light leading-tight">
                          {opt.label}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mt-1.5">
                          {opt.tagline}
                        </div>
                      </div>
                      {selected && (
                        <span
                          aria-hidden
                          className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[hsl(var(--gold))] text-background text-[10px] font-semibold"
                        >
                          ✓
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                      {opt.desc}
                    </p>

                    {/* Feature bullets — icon + label, tight rhythm */}
                    <ul className="mt-4 space-y-2">
                      {opt.bullets.map((b, i) => {
                        const Icon = b.icon;
                        return (
                          <li key={i} className="flex items-center gap-2.5 text-xs text-foreground/85">
                            <Icon className="w-3.5 h-3.5 text-[color:var(--gold-base)] shrink-0" />
                            <span>{b.text}</span>
                          </li>
                        );
                      })}
                    </ul>

                    {/* "Best for" footer */}
                    <p className="mt-4 pt-3 border-t border-border/60 text-[11px] italic font-serif text-muted-foreground/80 leading-relaxed">
                      {opt.best}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Advanced (optional) */}
          <div className="border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground flex items-center gap-1.5"
              data-testid="toggle-advanced-btn"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {showAdvanced ? "Hide" : "Show"} advanced options
              <span className="text-[10px] opacity-60">(optional)</span>
            </button>

            {showAdvanced && (
              <div className="space-y-4 mt-4 bg-muted/30 p-4" data-testid="advanced-section">
                <div className="grid grid-cols-2 gap-3">
                  <In label="View password" value={adv.password} onChange={(v) => setAdv({ ...adv, password: v })} testid="collection-password" placeholder="Optional" />
                  <In label="Download password" value={adv.download_password} onChange={(v) => setAdv({ ...adv, download_password: v })} testid="collection-download-password" placeholder="Optional" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <In label="Included edits" type="number" value={adv.included_edits} onChange={(v) => setAdv({ ...adv, included_edits: v })} testid="collection-included-edits" />
                  <In label="Extra edit ($)" type="number" value={adv.extra_edit_price} onChange={(v) => setAdv({ ...adv, extra_edit_price: v })} testid="collection-extra-price" />
                  <In label="Max selections" type="number" value={adv.max_selections} onChange={(v) => setAdv({ ...adv, max_selections: v })} testid="collection-max-sel" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="eyebrow">Cover layout</span>
                    <div className="mt-1">
                      <EditorialSelect
                        value={adv.cover_layout}
                        onChange={(v) => setAdv({ ...adv, cover_layout: v })}
                        options={["center", "left", "novel", "vintage", "frame", "stripe", "divider", "journal"].map((l) => ({
                          value: l,
                          label: l.charAt(0).toUpperCase() + l.slice(1),
                        }))}
                        data-testid="collection-cover-layout"
                      />
                    </div>
                  </label>
                  <In label="Category tags (comma-sep)" value={adv.category_tags} onChange={(v) => setAdv({ ...adv, category_tags: v })} testid="collection-tags" placeholder="maternity, family" />
                </div>
                <label className="block">
                  <span className="eyebrow">Welcome message</span>
                  <textarea
                    rows={2}
                    value={adv.welcome_message}
                    onChange={(e) => setAdv({ ...adv, welcome_message: e.target.value })}
                    className="mt-1 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground text-sm"
                    data-testid="collection-welcome"
                  />
                </label>
                <p className="text-[11px] text-muted-foreground italic">
                  These settings are remembered for next time. You can change any of them later in the collection&apos;s Settings tab.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-3 pt-4 border-t border-border mt-4">
            <button
              onClick={onClose}
              className="px-5 h-11 text-[11px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center justify-center px-8 h-11 text-[11px] uppercase tracking-[0.28em] bg-[#C9A961] text-stone-900 font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="create-collection-btn"
            >
              {submitting ? "Creating…" : "Create collection"}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>

      {/* What's the difference? — richer side-by-side explainer modal.
          Triggered from the gallery-type section. Designed to read like a
          spread from a print quarterly: two columns, no marketing fluff,
          concrete feature parity and "best for" guidance at the bottom. */}
      <GalleryTypeInfoModal
        open={showGalleryTypeInfo}
        onClose={() => setShowGalleryTypeInfo(false)}
        currentType={adv.type || "standard"}
        onPick={(type) => {
          setAdv({ ...adv, type });
          setShowGalleryTypeInfo(false);
        }}
      />
    </Dialog>
  );
}

/* GalleryTypeInfoModal — full Standard-vs-Interactive comparison. Opened
   from the "What's the difference?" link inside the create-collection form.
   The form's inline cards are the primary picker; this modal is the deep-dive
   for first-time users + anyone who wants to read more before deciding. */
function GalleryTypeInfoModal({ open, onClose, currentType, onPick }) {
  if (!open) return null;

  const ROWS = [
    { label: "Password protection",       std: "Single optional password",       int: "Single optional password" },
    { label: "Photo viewing",             std: "Full grid + lightbox",           int: "Full grid + lightbox" },
    { label: "Original downloads",        std: "On (optional 2nd password)",     int: "On (optional 2nd password)" },
    { label: "Primary client invites",    std: "—",                              int: "Yes — invite family + friends" },
    { label: "Favorites / hearts",        std: "—",                              int: "Yes, per guest" },
    { label: "Comments per frame",        std: "—",                              int: "Yes (private to client + you)" },
    { label: "Live watch-together",       std: "—",                              int: "Yes — share a live cursor session" },
    { label: "Selection limits",          std: "Optional max-selection cap",     int: "Optional, with per-guest tracking" },
    { label: "Best for",                  std: "Finished weddings, brand deliveries, anything where the client just needs to receive.", int: "Proofing rounds, family albums, anything where the client involves others in choosing." },
  ];

  return (
    <div
      className="fixed inset-0 z-[70] bg-background/85 backdrop-blur-sm flex items-center justify-center px-5 py-10 overflow-y-auto"
      data-testid="gallery-type-info-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-3xl bg-card border border-border rounded-sm shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-7 pt-7 pb-5 border-b border-border">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors p-1.5"
            data-testid="gallery-type-info-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">
            Lumetier · Gallery types
          </div>
          <h2 className="mt-3 font-serif font-light leading-tight" style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>
            Two ways to send a collection.
          </h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-xl">
            Both are private, password-friendly, mobile-perfect. The difference is whether the gallery is a one-way handoff or a shared room.
          </p>
        </div>

        {/* Comparison table — two narrow columns */}
        <div className="grid grid-cols-[1.1fr_1fr_1fr] divide-x divide-border text-sm" data-testid="gallery-type-info-table">
          {/* Column headers */}
          <div className="px-5 py-4 bg-muted/30" aria-hidden />
          <div className="px-5 py-4 bg-muted/30">
            <div className="font-serif text-lg italic">Standard</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)] mt-1">Deliver. Done.</div>
          </div>
          <div className="px-5 py-4 bg-muted/30">
            <div className="font-serif text-lg italic">Interactive</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)] mt-1">Choose. Together.</div>
          </div>

          {/* Feature rows */}
          {ROWS.map((row, i) => (
            <div className="contents" key={i}>
              <div className="px-5 py-3 text-muted-foreground border-t border-border text-[13px]">{row.label}</div>
              <div className="px-5 py-3 border-t border-border text-[13px] text-foreground/90 leading-snug">{row.std}</div>
              <div className="px-5 py-3 border-t border-border text-[13px] text-foreground/90 leading-snug">{row.int}</div>
            </div>
          ))}
        </div>

        {/* CTA footer — pick from the modal directly so the user doesn't
            have to close + re-find the cards. */}
        <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
          <button
            type="button"
            onClick={() => onPick("standard")}
            data-testid="gallery-type-info-pick-standard"
            className={`px-6 py-4 text-[10px] uppercase tracking-[0.32em] transition-colors text-left ${
              currentType === "standard"
                ? "bg-[hsl(var(--gold))]/12 text-foreground"
                : "hover:bg-muted/30 text-muted-foreground hover:text-foreground"
            }`}
          >
            <div className="font-serif italic text-base text-foreground normal-case tracking-normal">
              {currentType === "standard" ? "✓ Standard selected" : "Use Standard"}
            </div>
            <div className="mt-1.5 text-muted-foreground/80">Click to pick and close</div>
          </button>
          <button
            type="button"
            onClick={() => onPick("interactive")}
            data-testid="gallery-type-info-pick-interactive"
            className={`px-6 py-4 text-[10px] uppercase tracking-[0.32em] transition-colors text-left ${
              currentType === "interactive"
                ? "bg-[hsl(var(--gold))]/12 text-foreground"
                : "hover:bg-muted/30 text-muted-foreground hover:text-foreground"
            }`}
          >
            <div className="font-serif italic text-base text-foreground normal-case tracking-normal">
              {currentType === "interactive" ? "✓ Interactive selected" : "Use Interactive"}
            </div>
            <div className="mt-1.5 text-muted-foreground/80">Click to pick and close</div>
          </button>
        </div>
      </div>
    </div>
  );
}

// localStorage helpers for remembering last-used advanced settings
const ADV_KEY = "allure_collection_adv_defaults";
function loadAdvancedDefaults() {
  try {
    const raw = localStorage.getItem(ADV_KEY);
    if (raw) return { ...DEFAULT_ADV, ...JSON.parse(raw) };
  } catch (err) { console.warn("adv defaults load failed", err); }
  return { ...DEFAULT_ADV };
}
function saveAdvancedDefaults(adv) {
  try { localStorage.setItem(ADV_KEY, JSON.stringify(adv)); } catch (err) { console.warn("adv defaults save failed", err); }
}
const DEFAULT_ADV = {
  password: "", download_password: "", included_edits: 0, extra_edit_price: 35,
  max_selections: 0, welcome_message: "", category_tags: "", cover_layout: "center",
  type: "standard",
};

function Tab({ active, onClick, children, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 h-9 text-xs uppercase tracking-[0.18em] border ${active ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/60"} disabled:opacity-40 disabled:cursor-not-allowed`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function In({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        data-testid={testid}
      />
    </label>
  );
}
